import { ColumnDef } from '@tanstack/react-table';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, Divider, Text, useColorMode } from 'theme-ui';
import { RootState, IMasterDataUser, IMasterDataForTable } from '../../../../@types';
import { getMyReviews } from '../../../store/actions/Rating.Action';
import { UpdateFrontAppRow } from '../../../store/actions/frontApp.Action';
import { getApiEndPoint } from '../../../utils/GenerateQueryString';
import { baseUrl } from '../../../utils/global';
import DataTable from '../../Tabels/Tabel';
import { TabelHeadingTxt } from '../../Text/TabelCommonTxt';
import exprt from "../../assets/images/Icon/Export.svg";
import { useQuery } from '@tanstack/react-query';
import { FaStar } from 'react-icons/fa';
import { setPagination } from '../../../store/actions/Pagination.action';
import moment from 'moment';
import { isNumber } from 'lodash';
// import { getAllFrontapp } from '../../../store/actions/frontApp.Action';
const MyReviews: React.FC = () => {
    const location = useLocation();
    const { MyRating, loader } = useSelector((state: RootState) => state?.rating);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dispatch = useDispatch()
    const [colorMode] = useColorMode();
    const starHeader = colorMode === 'light' ? "#0a0a0a" : "#dadada";
    const [searchParams, setSearchParams] = useSearchParams({
        currentPage: "1",
        limit: "15",
    });
    const { dataForTable } = useSelector((state: RootState) => state.masterData) as { dataForTable: IMasterDataForTable | null | undefined };
    const { auther } = useSelector((state: RootState) => state.auth);

    const roles: string[] = auther?.Role?.map((r) => r?.RoleName) || [];
    const isOnlyTaskWorker = roles.length > 0 && roles.every((r: string) => r === 'Task Worker');
    const isOnlyTaskManager = roles.length > 0 && roles.every((r: string) => r === 'Task Manager');
    const isTaskWorker = roles.includes('Task Worker');
    const isTaskManager = roles.includes('Task Manager');
    const hasHigherRole = roles.some((r: string) =>
        ['project manager', 'super admin', 'region admin', 'ceo', 'coo', 'principal (pe)'].includes(r?.toLowerCase()?.trim())
    );
    const isTeamLead = roles.some((r: string) => r?.toLowerCase()?.trim() === 'team lead');
    // Has both Task Worker AND Task Manager but not a higher-level role
    const isTaskManagerAndWorker = isTaskWorker && isTaskManager && !hasHigherRole;

    const limit = parseInt(searchParams.get('limit') || "15");
    const currentPage = parseInt(searchParams.get('currentPage') || "1");

    const formatCriteriaName = (name: string) => {
        if (!name) return "";
        const afterUnderscore = name.split("_").pop() || "";
        const beforeParenthesis = afterUnderscore.split("(")[0] || "";
        return beforeParenthesis.trim();
    };

    const taskManagerOptions = useMemo(() => {
        const autherDepartments = auther?.departments || [];
        const isTaskManagerOrTeamLead = auther?.Role?.some((role) =>
            ['task manager', 'team lead'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const hasHigherRole = auther?.Role?.some((role) =>
            ['project manager', 'super admin', 'region admin', 'ceo', 'coo', 'principal (pe)'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const applyDepartmentFilter = isTaskManagerOrTeamLead && !hasHigherRole;
        const autherDeptIds = autherDepartments.map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);

        const managers = (dataForTable?.data?.allTaskManagers || []).filter((manager: IMasterDataUser) => {
            if (!applyDepartmentFilter) return true;
            if (!autherDeptIds?.length) return true;
            const managerDeptIds = (manager?.departments || []).map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);
            return autherDeptIds.some((deptId) => managerDeptIds.includes(deptId));
        });

        if (managers.length === 0 && (dataForTable?.data?.allTaskManagers || []).length > 0) {
            return (dataForTable?.data?.allTaskManagers || []).map((val: IMasterDataUser) => ({
                value: val._id,
                label: `${val.FirstName} ${val.LastName}`
            }));
        }

        return managers.map((val: IMasterDataUser) => ({
            value: val._id,
            label: `${val.FirstName} ${val.LastName}`
        }));
    }, [dataForTable?.data?.allTaskManagers, auther]);
    const taskWorkerOptions = useMemo(() => {
        const autherDepartments = auther?.departments || [];
        const isTaskManagerOrTeamLead = auther?.Role?.some((role) =>
            ['task manager', 'team lead'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const hasHigherRole = auther?.Role?.some((role) =>
            ['project manager', 'super admin', 'region admin', 'ceo', 'coo', 'principal (pe)'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const applyDepartmentFilter = isTaskManagerOrTeamLead && !hasHigherRole;
        const autherDeptIds = autherDepartments.map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);

        const workers = (dataForTable?.data?.allTaskWorkers || []).filter((worker: IMasterDataUser) => {
            if (!applyDepartmentFilter) return true;
            if (!autherDeptIds?.length) return true;
            const workerDeptIds = (worker?.departments || []).map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);
            return autherDeptIds.some((deptId) => workerDeptIds.includes(deptId));
        });

        if (workers.length === 0 && (dataForTable?.data?.allTaskWorkers || []).length > 0) {
            return (dataForTable?.data?.allTaskWorkers || []).map((val: IMasterDataUser) => ({
                value: val._id,
                label: `${val.FirstName} ${val.LastName}`
            }));
        }

        return workers.map((val: IMasterDataUser) => ({
            value: val._id,
            label: `${val.FirstName} ${val.LastName}`
        }));
    }, [dataForTable?.data?.allTaskWorkers, auther]);
    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            header: 'No',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh'>{((currentPage - 1) * limit) + row.index + 1}</Text>
                )
            }
        },
        {
            accessorKey: "projectName",
            header: 'Project Name',
            cell: ({ row }) => {
                return (
                    <Text>{row?.original?.projectName}</Text>
                )
            },
            filterType: "textbox"
        },
        // {
        //     accessorKey: "tradeName",
        //     header: 'Trade Name',queryString
        //     cell: ({ row }) => {
        //         return (
        //             <p>{row?.original?.tradeName}</p>
        //         )
        //     },
        //     filterType: 'textbox',
        // },
        // {
        //     header: 'Worker',
        //     accessorKey: "workerName",
        //     cell: ({ row }) => {

        //         return (
        //             <p>
        //                 {row?.original?.workerName}
        //             </p>
        //         )
        //     },
        //     filterType: 'textbox',

        // },
        {
            header: 'Task Name',
            accessorKey: "tradeName",
            id: 'tradeName',
            cell: ({ row }) => {
                return (
                    <Text >{row?.original?.tradeName}</Text>
                )
            },
            filterType: 'textbox',
            // enableColumnFilter: true,
            // filterType: 'dropdown',
            // options: dataForTable?.data?.allTasks?.map((val: { _id: string, FirstName: string }) => ({ value: val._id, label: val.FirstName }))
        },
        {
            header: 'Task Manager',
            accessorKey: "taskManager",
            id: 'taskManager',
            cell: ({ row }: { row: any }) => {
                const managerId = row.original.ratingGivenByRef;
                const exists = dataForTable?.data?.allTaskManagers?.some((x: { _id: string }) => x._id === managerId);
                return (
                    <Text >{exists ? row?.original?.taskManager : ""}</Text>
                )
            },
            ...(hasHigherRole || isTeamLead ? {
                filterType: 'dropdown',
                options: taskManagerOptions
            } : { enableColumnFilter: false })
        },
        {
            header: 'Task Worker',
            accessorKey: "workerName",
            id: 'taskWorker',
            cell: ({ row }: { row: any }) => {
                return (
                    <Text>{row?.original?.workerName}</Text>
                )
            },
            ...(!isOnlyTaskWorker ? {
                filterType: 'dropdown',
                options: taskWorkerOptions
            } : { enableColumnFilter: false })
        },

        {
            header: 'Phase',
            accessorKey: "projectPhaseLabel",
            cell: ({ row }) => {
                return (
                    <p>{row?.original?.projectPhaseLabel}</p>
                )
            },
            filterType: 'textbox',
        },
        {
            header: 'Review Date',
            accessorKey: "createdAt",
            cell: ({ row }) => {
                const reviewDate = row?.original?.createdAt;
                return (
                    <p>{reviewDate ? moment(reviewDate.split('T')[0]).format("MMM DD, YYYY") : ""}</p>
                )
            },
            filterType: 'date',
        },
        {
            header: 'Criteria',
            accessorKey: "criteria",
            cell: ({ row }) => {
                return (
                    <Box sx={{ minWidth: '220px' }}>
                        {row?.original?.criteria?.map((criteria: any, index: any) => (
                            <Box
                                key={index}
                                sx={{
                                    alignItems: 'center',
                                    borderTop: index !== 0 ? "0.5px solid #D3D5D9" : "none",
                                    paddingY: "8px",
                                    paddingX: "10px",
                                    gap: "10px"
                                }}
                            >
                                <Text sx={{ fontWeight: "400", color: starHeader }}>
                                    {formatCriteriaName(criteria?.name)}{" "}({isNumber(criteria?.rating) ? criteria?.rating : 0})
                                </Text>
                                <Box sx={{ gap: '2px' }}>
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} size={16} fill={i < Math.floor(Number(criteria?.rating || 0)) ? "#FFB721" : "#8D8D8D"} />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                );
            },
            filterType: 'textbox'
        },
    ], [currentPage, limit, isOnlyTaskWorker, isTaskManagerAndWorker, hasHigherRole, isTeamLead, taskManagerOptions, taskWorkerOptions]);


    const activeWorkerIds = useMemo(() =>
        new Set((dataForTable?.data?.allTaskWorkers || []).map((w: { _id: string }) => w._id)),
        [dataForTable?.data?.allTaskWorkers]
    );

    const filteredData = useMemo(() =>
        (MyRating?.data || []).filter((row: any) => activeWorkerIds.has(row.workerNameRef)),
        [MyRating?.data, activeWorkerIds]
    );
    useEffect(() => {
        return () => {
            dispatch(setPagination({
                limit: 15,
                currentPage: 1,
            }))
        }
    }, [dispatch])
    useEffect(() => {
        let apiEndPoint = getApiEndPoint({ limit, currentPage }, location.search, columns?.map((val: any) => ({ accessorKey: val.accessorKey, filterType: val.filterType })) || [])
        dispatch(getMyReviews(apiEndPoint))
    }, [location?.search, limit, currentPage]);

    useEffect(() => {
        let queryValueInObj = queryString.parse(location.search);
        if (queryValueInObj["id"]) {
            let idValue: string = Array.isArray(queryValueInObj["id"])
                ? queryValueInObj["id"][0] || "" // Use the first element or provide a default value
                : queryValueInObj["id"] || "";   // Handle the case where it might be null

            setIsOpen(true);
            // setStageId(idValue);
        }
    }, [location.search]);

    const { data: eventSource } = useQuery({
        queryKey: ['frontAppSSE1'],
        queryFn: () => {
            const eventSource = new EventSource(`${baseUrl}sse/frontApp`);

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    dispatch(UpdateFrontAppRow(data))
                    // handleProjectUpdate(data); // Uncomment this line if you have a function to handle the project update
                } catch (error) {
                    console.error('Error parsing SSE data:', error);
                }
            };

            eventSource.onerror = (error) => {
                console.error('SSE Error:', error);
                eventSource.close();
            };
            return eventSource;
        },
        refetchInterval: 60000
    })
    useEffect(() => {
        return () => {
            eventSource?.close();
        };
    }, [])


    return (
        <>
            <div className='main-content'>
                <TabelHeadingTxt Title='Rating Review' Count={MyRating?.count} IsExportButtonShow={true} src={exprt} BtnTxt='Export' url='/my-review' ml={'60px'} />
                <Divider sx={{ color: '#D9D7DD' }} opacity={2} my={[3, 3, 4]} />
                <DataTable
                    loader={loader}
                    columns={columns}
                    data={filteredData}
                    TotalLength={MyRating?.count || 0}
                    perPageCount={limit}
                    currentPage={currentPage}
                    onPerPageChange={(itemsPerPage: string) => {
                        if (searchParams?.size > 2) {
                            const parsedQuery = queryString.parse(location.search);

                            setSearchParams({
                                ...parsedQuery,
                                limit: itemsPerPage.toString(),
                            });
                        } else {
                            setSearchParams({
                                currentPage: currentPage.toString(),
                                limit: itemsPerPage.toString(),
                            });
                        }
                    }}
                    onPageChange={(value: any) => {
                        if (searchParams?.size > 2) {
                            const parsedQuery = queryString.parse(location.search);

                            setSearchParams({
                                ...parsedQuery,
                                currentPage: value.toString(),
                            });
                        } else {
                            setSearchParams({
                                currentPage: value.toString(),
                                limit: limit.toString(),
                            });
                        }
                    }}
                />
            </div>
        </>
    )
};

export default MyReviews;
